import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { ROUTE_LIVEBETTING } from '../router/constants.js';
import useTranslations from '../translation/use-translations';
import { selectBrand } from '../brand/ducks/selectors.js';

// default textkeys for SEO content
const SPORTSBOOK_SITE_TITLE = 'SPORTSBOOK_SITE_TITLE';
const LIVEBETTING_SITE_TITLE = 'LIVEBETTING_SITE_TITLE';
const SPORTSBOOK_META_DESCRIPTION = 'SPORTSBOOK_META_DESCRIPTION';
const LIVEBETTING_META_DESCRIPTION = 'LIVEBETTING_META_DESCRIPTION';
const SPORTSBOOK_PAGE_MISSING_TITLE = 'SPORTSBOOK_PAGE_MISSING_TITLE';

const SportsbookSeoHeader = () => {
	const { pathname } = useLocation();
	const { routesSEO } = useSelector(selectBrand);

	const lowerCasePathname = pathname ? pathname.toLowerCase() : '';
	let prefixedDefaultTitle = '';

	const buildTextKeys = useCallback(() => {
		// assign default values
		let siteTitleTextKey;
		let metaDescriptionsTextKey;
		let sectionPrefix;

		switch (lowerCasePathname) {
			case ROUTE_LIVEBETTING:
				siteTitleTextKey = LIVEBETTING_SITE_TITLE;
				metaDescriptionsTextKey = LIVEBETTING_META_DESCRIPTION;
				break;
			default:
				siteTitleTextKey = SPORTSBOOK_SITE_TITLE;
				metaDescriptionsTextKey = SPORTSBOOK_META_DESCRIPTION;
		}

		const pathSegments = lowerCasePathname.split('/');

		if (pathSegments.length > 2) {
			const seoPathSegment = pathSegments[2]?.toUpperCase();
			if (seoPathSegment) {
				sectionPrefix = `SPORTSBOOK_${seoPathSegment}`;
				siteTitleTextKey = `${sectionPrefix}_SITE_TITLE`;
				metaDescriptionsTextKey = `${sectionPrefix}_META_DESCRIPTION`;
			}

			// overwrite title and metas if defined in routesSEO property
			const pathRelatedTextKey = routesSEO[lowerCasePathname];
			if (pathRelatedTextKey) {
				sectionPrefix = pathRelatedTextKey.replace('CONTENT_COMPONENT_', '');
				siteTitleTextKey = `${sectionPrefix}_SITE_TITLE`;
				metaDescriptionsTextKey = `${sectionPrefix}_META_DESCRIPTION`;
			}
		}
		return [siteTitleTextKey, metaDescriptionsTextKey, SPORTSBOOK_PAGE_MISSING_TITLE];
	}, [lowerCasePathname, routesSEO]);

	const textKeys = buildTextKeys();
	const { translations, isLoadingTranslations } = useTranslations(textKeys);

	const titleTextKey = textKeys.find(textKey => textKey.endsWith('_SITE_TITLE'));
	const metaDescriptionTextKey = textKeys.find(textKey => textKey.endsWith('_META_DESCRIPTION'));
	const validateContent = useCallback(
		textKeyName => {
			const translatedValue = translations[textKeyName];
			return !(
				(translatedValue?.startsWith('??') && translatedValue?.endsWith('??')) ||
				translatedValue?.length === 0
			);
		},
		[translations]
	);

	const translatedTitle = useMemo(() => {
		if (!isLoadingTranslations) {
			if (validateContent(titleTextKey)) {
				return translations[titleTextKey];
			}
			// possible add some log here to spot important content missing
			return null;
		}
	}, [isLoadingTranslations, titleTextKey, translations, validateContent]);

	const translatedMetaDescription = useMemo(() => {
		if (!isLoadingTranslations) {
			return validateContent(metaDescriptionTextKey) ? translations[metaDescriptionTextKey] : '';
		}
	}, [isLoadingTranslations, metaDescriptionTextKey, translations, validateContent]);

	if (!isLoadingTranslations && !translatedTitle) {
		// assign a default value when it can't translate the title
		prefixedDefaultTitle = translations[SPORTSBOOK_SITE_TITLE];
		// add default text prefixed with section name in order to make unique and avoid content duplication
		const pathSegments = lowerCasePathname.split('/');
		if (pathSegments.length > 2) {
			const seoPathSegment = pathSegments[2]?.toUpperCase();
			prefixedDefaultTitle = `${seoPathSegment} ${translations[SPORTSBOOK_PAGE_MISSING_TITLE]}`;
		}
	}

	if (isLoadingTranslations) {
		return null;
	}

	return (
		<Helmet>
			{translatedMetaDescription && <meta name="description" content={`${translatedMetaDescription}`} />}
			{translatedTitle && <title>{translatedTitle}</title>}
			{prefixedDefaultTitle && !translatedTitle && <title>{prefixedDefaultTitle}</title>}
		</Helmet>
	);
};

export default SportsbookSeoHeader;
